import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { api, readBlobError } from '../api/client';
import { useAuth } from '../context/AuthContext';
import PaymentDetailsBlock from './PaymentDetailsBlock';

interface PaymentQRCodeProps {
  eventId: string;
  memberId: string;
  size?: number;
}

/**
 * Component that fetches and displays a payment QR code with authentication.
 * Uses the API client to include auth headers in the request.
 */
const PaymentQRCode: React.FC<PaymentQRCodeProps> = ({ eventId, memberId, size = 150 }) => {
  const { t } = useTranslation();
  const { isAdmin } = useAuth();
  const [qrUrl, setQrUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [errorCode, setErrorCode] = useState<string | null>(null);

  useEffect(() => {
    let blobUrl: string | null = null;

    const fetchQR = async () => {
      try {
        // Admins use the gated admin QR route; everyone else (members and
        // logged-in public-event registrants) uses the policy-gated /my route.
        // NOTE: on the public event page an anonymous (logged-out) registrant
        // has no token, so neither route returns a QR — that was already the
        // case before the split (the admin route required auth too). A
        // logged-in member registering still gets their QR via /my.
        const response = isAdmin
          ? await api.payments.getQR(eventId, memberId)
          : await api.my.payments.qr(eventId, memberId);
        blobUrl = URL.createObjectURL(response.data as Blob);
        setQrUrl(blobUrl);
        setError(false);
        setErrorCode(null);
      } catch (err) {
        const { code } = await readBlobError(err);
        setErrorCode(code ?? null);
        setError(true);
      } finally {
        setLoading(false);
      }
    };
    fetchQR();

    // Cleanup blob URL on unmount
    return () => {
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [eventId, memberId, isAdmin]);

  if (loading) {
    return (
      <div style={{
        width: size,
        height: size,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#f0f0f0',
        borderRadius: '4px',
        color: '#666',
        fontSize: '0.8rem'
      }}>
        Loading...
      </div>
    );
  }

  if (error || !qrUrl) {
    return (
      <div style={{
        width: size,
        height: size,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#f0f0f0',
        borderRadius: '4px',
        color: '#666',
        fontSize: '0.75rem',
        textAlign: 'center',
        padding: '10px'
      }}>
        {errorCode === 'BANK_NOT_CONFIGURED'
          ? (isAdmin ? t('payments.qrBankNotConfiguredAdmin') : t('payments.qrUnavailable'))
          : errorCode === 'PAYMENT_SETTLED'
            ? t('payments.qrAlreadyPaid')
            : t('payments.qrUnavailable')}
      </div>
    );
  }

  return (
    <div style={{ maxWidth: size, width: '100%' }}>
      <img
        src={qrUrl}
        alt="Payment QR"
        style={{ width: '100%', maxWidth: size, aspectRatio: '1 / 1', display: 'block' }}
      />
      <PaymentDetailsBlock
        type="event"
        entityId={eventId}
        memberId={memberId}
        isAdmin={isAdmin}
      />
    </div>
  );
};

export default PaymentQRCode;
